import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Modal, Row, Col, Input, Select, Form, Button, InputNumber, Divider } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { AiOutlineSearch } from 'react-icons/ai';
import TableComponent from 'components/table-component';
import { SERVER_IP } from 'assets/Config';
import { getApi } from 'redux/sagas/getApiDataSaga';
import { postApi } from 'redux/sagas/postApiDataSaga';
import { putApi } from 'redux/sagas/putApiSaga';
import { deleteApi } from 'redux/sagas/deleteApiSaga';
import { resetApiStatus } from 'redux/reducers/globals/globalActions';
import { API_STATUS } from 'constants/app-constants';
import { formQueryStringFromObject } from 'helpers';
import { useI18n } from 'hooks/useI18n';

const layer1FormCol = {
	labelCol: { span: 24 },
	wrapperCol: { span: 24 },
};

// --------------------- MembershipSelect ---------------------
const MembershipSelect = ({ value, onChange, openModal, serviceId }) => {
	const memberships = useSelector((state) => state.membershipRedux?.memberships || []);
	return (
		<Select
			placeholder="Select Membership"
			value={value}
			onChange={onChange}
			disabled={!serviceId}
			showSearch
			optionFilterProp="children"
			filterOption={(input, option) => option.children?.toLowerCase().includes(input.toLowerCase())}
			dropdownRender={(menu) => (
				<>
					{menu}
					<Divider style={{ margin: 0 }} />
					<div style={{ display: 'flex', flexWrap: 'nowrap', cursor: 'pointer' }} onClick={openModal}>
						<a style={{ flex: 'none', color: '#188dfa', padding: 8, display: 'block' }}>
							<PlusOutlined /> Membership
						</a>
					</div>
				</>
			)}>
			{memberships.map((m) => (
				<Select.Option key={m.id} value={m.id}>
					{m.membershipName}
				</Select.Option>
			))}
		</Select>
	);
};

// --------------------- MembershipForm ---------------------
const MembershipForm = ({ form, onSubmit, loading, editData, services, serviceId, onCancel }) => (
	<Form layout="vertical" form={form} onFinish={onSubmit} {...layer1FormCol}>
		<Row gutter={[10, 0]}>
			{!serviceId && (
				<Col span={12}>
					<Form.Item label="Service" name="serviceId" rules={[{ required: true }]}>
						<Select placeholder="Select service">
							{services.map((s) => (
								<Select.Option key={s.id} value={s.id}>
									{s.serviceName}
								</Select.Option>
							))}
						</Select>
					</Form.Item>
				</Col>
			)}
			<Col span={12}>
				<Form.Item label="Membership Name" name="membershipName" rules={[{ required: true }]}>
					<Input placeholder="Enter membership name" />
				</Form.Item>
			</Col>
			<Col span={12}>
				<Form.Item label="Days" name="days" rules={[{ required: true }]}>
					<InputNumber style={{ width: '100%' }} placeholder="Enter number of days" />
				</Form.Item>
			</Col>
			<Col span={12}>
				<Form.Item label="Original Price" name="originalPrice" rules={[{ required: true }]}>
					<InputNumber style={{ width: '100%' }} placeholder="Enter original price" />
				</Form.Item>
			</Col>
			<Col span={12}>
				<Form.Item label="Offer Price" name="offerPrice" rules={[{ required: true }]}>
					<InputNumber style={{ width: '100%' }} placeholder="Enter offer price" />
				</Form.Item>
			</Col>
		</Row>
		<Row justify="end" gutter={8}>
			<Button type="primary" htmlType="submit" loading={loading} style={{ marginRight: 8 }}>
				{editData ? 'Update' : 'Save'}
			</Button>
			<Button danger onClick={onCancel}>
				Cancel
			</Button>
		</Row>
	</Form>
);

// --------------------- MembershipTable ---------------------
const MembershipTable = ({ data, loading, onEdit, onDelete }) => {
	const { t } = useI18n();
	const columns = useMemo(
		() => [
			{ title: t('memberships.columns.membership_name'), dataIndex: 'membershipName', key: 'membershipName' },
			{ title: t('memberships.columns.days'), dataIndex: 'days', key: 'days' },
			{ title: t('memberships.columns.original_price'), dataIndex: 'originalPrice', key: 'originalPrice' },
			{ title: t('memberships.columns.offer_price'), dataIndex: 'offerPrice', key: 'offerPrice' },
			{
				title: t('memberships.columns.action'),
				key: 'actions',
				render: (_, record) => (
					<Row justify="center" gutter={8}>
						<Col>
							<Button type="link" icon={<EditOutlined />} onClick={() => onEdit(record)} />
						</Col>
						<Col>
							<Button type="link" danger icon={<DeleteOutlined />} onClick={() => onDelete(record.id)} />
						</Col>
					</Row>
				),
			},
		],
		[onEdit, onDelete, t]
	);

	return <TableComponent rowKey="id" loading={loading} columns={columns} dataSource={data} pagination={false} />;
};

// --------------------- Main MembershipModal ---------------------
const MembershipModal = ({ value, onChange, serviceId }) => {
	const [visible, setVisible] = useState(false);
	const [addFormVisible, setAddFormVisible] = useState(false);
	const [form] = Form.useForm();
	const [editData, setEditData] = useState(null);
	const [searchKey, setSearchKey] = useState('');
	const dispatch = useDispatch();

	const globalRedux = useSelector((state) => state.globalRedux);
	const branchId = globalRedux?.selectedBranch?.id;
	const services = useSelector((state) => state.serviceRedux?.services || []);
	const membershipList = useSelector((state) => state.membershipRedux?.memberships || []);
	const loading = globalRedux.apiStatus.GET_MEMBERSHIPS === API_STATUS.PENDING;
	const buttonLoading = globalRedux.apiStatus.ADD_MEMBERSHIP === API_STATUS.PENDING || globalRedux.apiStatus.EDIT_MEMBERSHIP === API_STATUS.PENDING;

	const getMemberships = useCallback(() => {
		const query = formQueryStringFromObject({ branchId, serviceId });
		dispatch(getApi('GET_MEMBERSHIPS', `${SERVER_IP}api/memberships?${query}`));
	}, [branchId, serviceId, dispatch]);

	const getServices = useCallback(() => {
		const query = formQueryStringFromObject({ branchId });
		dispatch(getApi('GET_SERVICES', `${SERVER_IP}api/services?${query}`));
	}, [branchId, dispatch]);

	useEffect(() => {
		if (visible) {
			getMemberships();
			getServices();
		}
	}, [visible, getMemberships, getServices]);

	const handleSubmit = (values) => {
		const data = {
			...values,
			branchId,
			serviceId: serviceId || values.serviceId,
		};
		if (editData) {
			dispatch(putApi(data, 'EDIT_MEMBERSHIP', `${SERVER_IP}api/memberships/${editData.id}`));
		} else {
			dispatch(postApi(data, 'ADD_MEMBERSHIP'));
		}
	};

	const handleDelete = (id) => {
		dispatch(deleteApi('DELETE_MEMBERSHIP', `${SERVER_IP}api/memberships/${id}`));
	};

	useEffect(() => {
		const status = globalRedux.apiStatus;
		if (status.ADD_MEMBERSHIP === 'SUCCESS' || status.EDIT_MEMBERSHIP === 'SUCCESS') {
			dispatch(resetApiStatus(editData ? 'EDIT_MEMBERSHIP' : 'ADD_MEMBERSHIP'));
			getMemberships();
			setAddFormVisible(false);
		}
		if (status.DELETE_MEMBERSHIP === 'SUCCESS') {
			dispatch(resetApiStatus('DELETE_MEMBERSHIP'));
			getMemberships();
		}
	}, [globalRedux.apiStatus]);

	useEffect(() => {
		if (!addFormVisible) {
			form.resetFields();
			setEditData(null);
		}
	}, [addFormVisible]);

	const filteredData = useMemo(() => {
		const search = searchKey.toLowerCase();
		return membershipList.filter((item) => item?.membershipName?.toLowerCase().includes(search) || item?.days?.toString().includes(search));
	}, [membershipList, searchKey]);

	return (
		<>
			<MembershipSelect value={value} onChange={onChange} openModal={() => setVisible(true)} serviceId={serviceId} />

			<Modal open={visible} title="Manage Memberships" onCancel={() => setVisible(false)} footer={null} destroyOnClose width="50%">
				{addFormVisible ? (
					<MembershipForm
						form={form}
						onSubmit={handleSubmit}
						loading={buttonLoading}
						editData={editData}
						services={services}
						serviceId={serviceId}
						onCancel={() => setAddFormVisible(false)}
					/>
				) : (
					<>
						<Row justify="space-between" style={{ marginBottom: 12 }}>
							<Col span={16}>
								<Input placeholder="Search Membership" onChange={(e) => setSearchKey(e.target.value)} suffix={<AiOutlineSearch />} />
							</Col>
							<Col>
								<Button type="primary" onClick={() => setAddFormVisible(true)}>
									Add New
								</Button>
							</Col>
						</Row>
						<MembershipTable data={filteredData} loading={loading} onEdit={(record) => {
							setEditData(record);
							form.setFieldsValue(record);
							setAddFormVisible(true);
						}} onDelete={handleDelete} />
					</>
				)}
			</Modal>
		</>
	);
};

export default MembershipModal;
